/**
 * Короткое видео к работе отчёта смены (ПР-03, МП-03).
 * Крупные ролики перекодируем в браузере (MediaRecorder → webm), иначе оставляем как есть.
 */
import { scrubMediaLocation } from './media-scrub';
import { sha256Hex } from './photo-sync';

export const VIDEO_MAX_SOURCE = 40 * 1024 * 1024;
/** Дольше минуты на площадке обычно не нужно и плохо уходит по 3G. */
export const VIDEO_MAX_SECONDS = 60;
/** Уже меньше этого — не трогаем (экономим батарею и время на площадке). */
export const VIDEO_SKIP_COMPRESS_BELOW = 8 * 1024 * 1024;
/** Целевой битрейт после сжатия (~1 Мбит/с хватает для короткого ролика работ). */
export const VIDEO_TARGET_BITRATE = 1_000_000;

const ALLOWED = new Set(['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v']);

export interface PreparedVideo {
  blob: Blob;
  sha256: string;
  size: number;
  mimeType: string;
  capturedAt: string | null;
  durationSec: number | null;
}

export type VideoDurationReader = (blob: Blob) => Promise<number | null>;
export type VideoCompressor = (blob: Blob) => Promise<Blob>;

/** Длительность через скрытый <video>; при ошибке — null (не блокируем съёмку). */
export const readVideoDuration: VideoDurationReader = (blob) =>
  new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    const el = document.createElement('video');
    el.preload = 'metadata';
    const done = (sec: number | null) => {
      URL.revokeObjectURL(url);
      el.removeAttribute('src');
      el.load();
      resolve(sec);
    };
    el.onloadedmetadata = () => {
      const d = el.duration;
      done(Number.isFinite(d) && d > 0 ? d : null);
    };
    el.onerror = () => done(null);
    el.src = url;
  });

function pickRecorderMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const mime of ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm']) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return null;
}

/**
 * Перекодирование через воспроизведение + MediaRecorder.
 * Не умеем — возвращаем исходник (снимок всё равно уйдёт, сжатие не блокирует).
 */
export const compressVideoInBrowser: VideoCompressor = async (input) => {
  if (input.size <= VIDEO_SKIP_COMPRESS_BELOW) return input;
  if (typeof document === 'undefined' || typeof MediaRecorder === 'undefined') return input;
  const recorderMime = pickRecorderMime();
  if (!recorderMime) return input;

  const url = URL.createObjectURL(input);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = url;

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Не удалось прочитать видео'));
    });
    type CaptureVideo = HTMLVideoElement & {
      captureStream?: () => MediaStream;
      mozCaptureStream?: () => MediaStream;
    };
    const capturable = video as CaptureVideo;
    const stream: MediaStream | null =
      typeof capturable.captureStream === 'function'
        ? capturable.captureStream()
        : typeof capturable.mozCaptureStream === 'function'
          ? capturable.mozCaptureStream()
          : null;
    if (!stream || stream.getVideoTracks().length === 0) return input;

    const chunks: BlobPart[] = [];
    const recorder = new MediaRecorder(stream, {
      mimeType: recorderMime,
      videoBitsPerSecond: VIDEO_TARGET_BITRATE,
    });
    const done = new Promise<Blob>((resolve, reject) => {
      recorder.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      recorder.onerror = () => reject(new Error('Сжатие видео не удалось'));
      recorder.onstop = () => resolve(new Blob(chunks, { type: 'video/webm' }));
    });
    recorder.start(200);
    await video.play();
    await new Promise<void>((resolve) => {
      video.onended = () => resolve();
      // страховка: если ended не пришёл
      const ms =
        Math.ceil((Number.isFinite(video.duration) ? video.duration : VIDEO_MAX_SECONDS) * 1000) + 2000;
      setTimeout(() => resolve(), ms);
    });
    if (recorder.state !== 'inactive') recorder.stop();
    for (const track of stream.getTracks()) track.stop();
    const out = await done;
    // Если стало не меньше — оставляем исходник
    if (!out.size || out.size >= input.size) return input;
    return out;
  } catch {
    return input;
  } finally {
    video.pause();
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
};

export async function prepareVideo(
  file: File,
  source: 'camera' | 'gallery',
  now: Date,
  readDuration: VideoDurationReader = readVideoDuration,
  compress: VideoCompressor = compressVideoInBrowser,
): Promise<PreparedVideo> {
  const mime = (file.type || '').toLowerCase();
  if (!ALLOWED.has(mime) && !mime.startsWith('video/')) {
    throw new Error('Можно прикладывать только видео (mp4, webm, mov)');
  }
  if (!mime.startsWith('video/')) throw new Error('Можно прикладывать только видео');
  if (file.size > VIDEO_MAX_SOURCE) {
    throw new Error('Видео больше 40 МБ. Снимите короче или с меньшим качеством');
  }
  if (!file.size) throw new Error('Видео пустое');
  const durationSec = await readDuration(file);
  if (durationSec != null && durationSec > VIDEO_MAX_SECONDS) {
    throw new Error(`Видео длиннее ${VIDEO_MAX_SECONDS} с. Снимите короче`);
  }
  // Запись места из ролика убирается всегда (МП-07): место, если есть согласие, хранится только в записи отчёта
  const blob = await scrubMediaLocation(await compress(file));
  if (!blob.size) throw new Error('Видео пустое');
  const outMime = (blob.type || mime).toLowerCase() || 'video/webm';
  const stamp = source === 'camera' ? now.getTime() : file.lastModified || 0;
  return {
    blob,
    sha256: await sha256Hex(blob),
    size: blob.size,
    mimeType: outMime.startsWith('video/') ? outMime : 'video/webm',
    capturedAt: stamp > 0 ? new Date(stamp).toISOString() : null,
    durationSec,
  };
}
